import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BaseCard from '../../ui/BaseCard';
import Like from '../../ui/Like';
import Badge from '../../ui/Badge';
import { createSelector } from '@reduxjs/toolkit';
import { formatCurrency, formatRentalPeriod } from '../../../utils/formatters';

// Memoized selector
const selectAuthState = createSelector(
  state => state.auth,
  auth => ({
    isLoggedIn: auth.isAuthenticated
  })
);

const BrowseCard = ({ product }) => {
  const history = useHistory();
  const { isLoggedIn } = useSelector(selectAuthState);

  const handleCardClick = useCallback((product) => {
    history.push('/product/' + product.id);
  }, [history]);

  // Detailed debugging of product data
  useEffect(() => {
    if (product) {
      console.log(`BrowseCard ${product.id || 'unknown'} - Full product data:`, {
        id: product.id,
        name: product.name,
        price: product.price,
        currency: product.currency,
        rentPriceAndTime: product.rentPriceAndTime
      });
    }
  }, [product]);

  if (!product || !product.activeListing || product.stock === '0') return null;
  
  // Make sure we always have a currency to work with
  const productCurrency = product.currency || 'USD';
  
  // Debug log product currency
  console.log(`BrowseCard ${product.id} - Price: ${product.price}, Currency: ${productCurrency}`);
  if (product.rentPriceAndTime && product.rentPriceAndTime.length > 0) {
    console.log(`BrowseCard ${product.id} - Rent Price: ${product.rentPriceAndTime[0].price}, Rent Currency: ${product.rentPriceAndTime[0].currency || productCurrency}`);
  }

  // Format sale price badge with correct currency
  const formatSalePrice = () => {
    return formatCurrency(product.price, productCurrency);
  };
  
  // Format rental price badge with correct currency
  const formatRentalPrice = () => {
    if (!product.rentPriceAndTime || product.rentPriceAndTime.length === 0) {
      return '';
    }
    
    const rentOption = product.rentPriceAndTime[0];
    const currency = rentOption.currency || productCurrency;
    
    return `${formatCurrency(rentOption.price, currency)}/${rentOption.number}${rentOption.option.charAt(0)}`;
  };

  return (
    <BaseCard
      product={product}
      onClick={handleCardClick}
      renderTopLeftContent={(product) => ( (
          <Badge 
            type={product.productType}
            size="sm"
            // className="bg-white/90"
          />
        ) 
      )}
      renderTopRightContent={(product) => (
        <Like 
          productId={product.id}
          size="sm"
          showCount={true}
          className="bg-white rounded-full shadow-sm p-1"
        />
      )}
      renderBottomContent={(product) => (
        <>
          <div className="flex flex-wrap gap-1 mt-2">
            {product.forSell && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {formatSalePrice()}
              </span>
            )}
            {product.forRent && product.rentPriceAndTime?.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {formatRentalPrice()}
              </span>
            )}
            {product.openToSwap && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Swap
              </span>
            )}
          </div>
        </>
      )}
    />
  );
};

export default BrowseCard; 